import { action } from '@ember/object';
import Component from '@glimmer/component';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import GeneralDatepickersFieldsDropdownsMonthRange from 'lumen/components/general/datepickers/fields/dropdowns-month-range';
import GeneralFormsFormFieldsDatepickerFormField from 'lumen/components/general/forms/form-fields/datepicker-form-field';
import type { DateGranularity } from 'lumen/utils/constants/budget-forecast-constants';

interface SettingsBudgetForecastModalBulkUpdateDateOrMonthRangeSignature {
  Args: {
    endDate: string;
    granularity: DateGranularity;
    maxDate: string;
    minDate: string;
    onRangeChange: (startDate: string, endDate: string) => void;
    startDate: string;
  };
}

export default class SettingsBudgetForecastModalBulkUpdateDateOrMonthRange extends Component<SettingsBudgetForecastModalBulkUpdateDateOrMonthRangeSignature> {
  get maxDateDayjs(): Dayjs {
    return dayjs(this.args.maxDate);
  }

  get minDateDayjs(): Dayjs {
    return dayjs(this.args.minDate);
  }

  get selectedMonthRange(): { end: Dayjs; start: Dayjs } {
    return {
      start: dayjs(this.args.startDate),
      end: dayjs(this.args.endDate),
    };
  }

  @action
  onEndDateChange(date: Dayjs | null): void {
    if (!date || !date.isValid()) {
      return;
    }

    const endDate = date.format('YYYY-MM-DD');

    this.args.onRangeChange(this.args.startDate, endDate);
  }

  @action
  onStartDateChange(date: Dayjs | null): void {
    if (!date || !date.isValid()) {
      return;
    }

    const startDate = date.format('YYYY-MM-DD');
    const endDate = this.validateEndDate(startDate, this.args.endDate);

    this.args.onRangeChange(startDate, endDate);
  }

  @action
  selectMonthRange({ moment }: { moment: { end: Dayjs; start: Dayjs } }): void {
    const startDate = moment.start.format('YYYY-MM-DD');
    const endDate = moment.end.format('YYYY-MM-DD');

    this.args.onRangeChange(startDate, endDate);
  }

  @action
  validateEndDate(startDate: string, endDate: string): string {
    const endDateDayjs = dayjs(endDate);

    if (endDateDayjs.isBefore(startDate)) {
      return this.args.maxDate;
    }

    return endDate;
  }

  <template>
    {{#if (eq @granularity "day")}}
      <div class="grid grid-cols-2 gap-400">
        <div>
          <GeneralFormsFormFieldsDatepickerFormField
            {{! @glint-expect-error: Incorrect type }}
            @datePickerMaxDate={{@maxDate}}
            @datePickerMinDate={{@minDate}}
            @dropdownClass="dropdown-target top-[56px]"
            @label={{t "general.start_date"}}
            @onSelectDate={{this.onStartDateChange}}
            @removePadding={{true}}
            @selectedDate={{@startDate}}
            @showDaysAround={{false}}
          />
        </div>
        <div>
          <GeneralFormsFormFieldsDatepickerFormField
            {{! @glint-expect-error: Incorrect type }}
            @datePickerMaxDate={{@maxDate}}
            @datePickerMinDate={{@startDate}}
            @dropdownClass="dropdown-target top-[56px]"
            @label={{t "general.end_date"}}
            @onSelectDate={{this.onEndDateChange}}
            @removePadding={{true}}
            @selectedDate={{@endDate}}
            @showDaysAround={{false}}
          />
        </div>
      </div>
    {{/if}}

    {{#if (eq @granularity "month")}}
      <GeneralDatepickersFieldsDropdownsMonthRange
        @isSecondary={{true}}
        {{! @glint-expect-error: Incorrect type }}
        @maxDate={{this.maxDateDayjs}}
        @maxRange="1 year"
        @minDate={{this.minDateDayjs}}
        @selectRange={{this.selectMonthRange}}
        @selectedRange={{this.selectedMonthRange}}
        @unsafe_matchTriggerWidth={{true}}
      />
    {{/if}}
  </template>
}
